import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 47. IDEMPOTENCE
// Les actions critiques doivent être idempotentes : si create_booking est
// envoyé deux fois (problème réseau, double clic…), on ne crée pas deux
// réservations. On s'appuie sur une clé d'idempotence fournie par l'appelant :
//
//   withIdempotency({ scope: 'reservations', key: '...' }, async () => {...})
//
// La clé est mémorisée dans core_idempotency_keys avec la réponse de la
// première exécution. Un second appel avec la même clé renvoie la réponse
// mémorisée au lieu de ré-exécuter l'action.
// ─────────────────────────────────────────────────────────────

/** Domaines critiques soumis à l'idempotence (spec 47). */
export const IDEMPOTENCY_SCOPES: { key: string; label: string; icon: string; className: string }[] = [
  { key: 'orders', label: 'Commandes', icon: 'ri-shopping-bag-3-line', className: 'bg-accent-100 text-accent-900' },
  { key: 'reservations', label: 'Réservations', icon: 'ri-hotel-line', className: 'bg-primary-100 text-primary-900' },
  { key: 'payments', label: 'Paiements', icon: 'ri-money-dollar-circle-line', className: 'bg-primary-100 text-primary-900' },
  { key: 'appointments', label: 'Rendez-vous', icon: 'ri-calendar-check-line', className: 'bg-secondary-100 text-secondary-900' },
  { key: 'invoices', label: 'Factures', icon: 'ri-file-list-3-line', className: 'bg-accent-100 text-accent-900' },
];

export function idempotencyScopeMeta(key: string | null | undefined) {
  return IDEMPOTENCY_SCOPES.find((s) => s.key === key) || {
    key: key || 'other',
    label: key || 'Autre',
    icon: 'ri-fingerprint-line',
    className: 'bg-background-100 text-foreground-700',
  };
}

export interface CoreIdempotencyKey {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  scope: string;
  idempotency_key: string;
  request_hash: string | null;
  entity_type: string | null;
  entity_id: string | null;
  status: string | null;
  response: Record<string, unknown> | null;
  created_at: string | null;
  expires_at: string | null;
}

/** Génère une clé d'idempotence côté client (unique, réutilisable en retry). */
export function generateIdempotencyKey(prefix = 'idem'): string {
  const rand = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID().replace(/-/g, '').slice(0, 20)
    : Math.random().toString(36).slice(2) + Date.now().toString(36);
  return `${prefix}_${rand}`;
}

/** Empreinte légère de la requête : détecte deux charges utiles différentes sur la même clé. */
export function hashRequest(payload: unknown): string {
  const str = JSON.stringify(payload ?? null);
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < str.length; i += 1) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (h2 >>> 0).toString(16).padStart(8, '0') + (h1 >>> 0).toString(16).padStart(8, '0');
}

export interface IdempotencyOutcome<T> {
  /** true si l'action n'a PAS été ré-exécutée (rejeu détecté). */
  duplicate: boolean;
  key: string;
  status: string;
  /** Réponse mémorisée lors du premier appel. */
  response: T | null;
  entityType: string | null;
  entityId: string | null;
}

export interface IdempotencyInput {
  tenantId: number | null;
  organizationId: number | null;
  scope: string;
  key: string;
  /** Charge utile servant à contrôler la cohérence (optionnel). */
  requestPayload?: unknown;
}

export interface IdempotencyExecution<T> {
  response: T;
  entityType?: string | null;
  entityId?: string | number | null;
}

/**
 * Exécute une action critique de façon idempotente.
 * - Si la clé n'existe pas : exécute l'action puis mémorise la réponse.
 * - Si la clé existe et est terminée : renvoie la réponse mémorisée (duplicate).
 * - Si un appel identique est en cours : renvoie duplicate (anti double traitement).
 * - Si l'action a échoué précédemment : elle peut être rejouée.
 */
export async function withIdempotency<T>(
  input: IdempotencyInput,
  executor: () => Promise<IdempotencyExecution<T>>,
): Promise<IdempotencyOutcome<T>> {
  const requestHash = hashRequest(input.requestPayload ?? null);

  // Rejoue-t-on une clé déjà vue et terminée ?
  const { data: existing } = await supabase
    .from('core_idempotency_keys')
    .select('*')
    .eq('organization_id', input.organizationId)
    .eq('scope', input.scope)
    .eq('idempotency_key', input.key)
    .maybeSingle();

  const row = existing as CoreIdempotencyKey | null;
  if (row && (row.status === 'completed' || row.status === 'in_progress')) {
    return {
      duplicate: true,
      key: input.key,
      status: row.status || 'completed',
      response: (row.response as T) ?? null,
      entityType: row.entity_type,
      entityId: row.entity_id,
    };
  }

  // Marque la clé en cours (anti double traitement concurrent).
  if (!row) {
    try {
      await supabase.from('core_idempotency_keys').insert({
        tenant_id: input.tenantId,
        organization_id: input.organizationId,
        scope: input.scope,
        idempotency_key: input.key,
        request_hash: requestHash,
        status: 'in_progress',
      });
    } catch {
      /* contrainte d'unicité (course) : on continue, la lecture ci-dessous tranche */
    }
  } else {
    await supabase
      .from('core_idempotency_keys')
      .update({ status: 'in_progress', request_hash: requestHash })
      .eq('id', row.id);
  }

  try {
    const exec = await executor();
    await supabase
      .from('core_idempotency_keys')
      .update({
        status: 'completed',
        response: exec.response as Record<string, unknown>,
        entity_type: exec.entityType ?? null,
        entity_id: exec.entityId != null ? String(exec.entityId) : null,
      })
      .eq('organization_id', input.organizationId)
      .eq('scope', input.scope)
      .eq('idempotency_key', input.key);

    return {
      duplicate: false,
      key: input.key,
      status: 'completed',
      response: exec.response,
      entityType: exec.entityType ?? null,
      entityId: exec.entityId != null ? String(exec.entityId) : null,
    };
  } catch (e) {
    // Échec : on repasse la clé en 'failed' pour autoriser un nouveau essai.
    await supabase
      .from('core_idempotency_keys')
      .update({ status: 'failed' })
      .eq('organization_id', input.organizationId)
      .eq('scope', input.scope)
      .eq('idempotency_key', input.key);
    throw e;
  }
}

export async function listIdempotencyKeys(organizationId: number, limit = 60): Promise<CoreIdempotencyKey[]> {
  const { data, error } = await supabase
    .from('core_idempotency_keys')
    .select('*')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data || []) as CoreIdempotencyKey[];
}

export interface IdempotencyStats {
  total: number;
  completed: number;
  inProgress: number;
  failed: number;
  duplicatesPrevented: number;
}

export async function fetchIdempotencyStats(organizationId: number): Promise<IdempotencyStats> {
  const { data } = await supabase
    .from('core_idempotency_keys')
    .select('status')
    .eq('organization_id', organizationId);
  let completed = 0;
  let inProgress = 0;
  let failed = 0;
  (data || []).forEach((r) => {
    const s = (r as { status: string | null }).status;
    if (s === 'completed') completed += 1;
    else if (s === 'in_progress') inProgress += 1;
    else if (s === 'failed') failed += 1;
  });
  return {
    total: (data || []).length,
    completed,
    inProgress,
    failed,
    duplicatesPrevented: completed + inProgress,
  };
}

export function idempotencyStatusMeta(status: string | null | undefined): { label: string; className: string } {
  switch (status) {
    case 'completed': return { label: 'Terminée', className: 'bg-primary-100 text-primary-900' };
    case 'in_progress': return { label: 'En cours', className: 'bg-accent-100 text-accent-900' };
    case 'failed': return { label: 'Échouée', className: 'bg-red-100 text-red-800' };
    default: return { label: status || '—', className: 'bg-background-100 text-foreground-500' };
  }
}